// src/App.js

import React, { useEffect } from 'react'; // <--- Import useEffect
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

// Config
import { AI_ENABLED } from './config/constants';

// Layout components
import Header from './layout/Header/Header';
import Footer from './layout/Footer/Footer';

// Landing
import CustomIndex from './components/CustomIndex/CustomIndex';
import Home from './components/Home/Home';

// Public content
import About from './components/About/About';
import Tradition from './components/Tradition/Tradition';
import Maps from './components/Maps/Maps';
import ContactUs from './components/ContactUs/ContactUs';
import JollyRogerClub from './components/JollyRogerClub/JollyRogerClub';

// Legal pages
import PrivacyPolicy from './components/Legal/PrivacyPolicy';
import Commitments from './components/Legal/Commitments';

// Authentication & user profile
import Register from './components/Register/Register';
import Login from './components/Login/Login';
import Profile from './components/Profile/Profile';
import Dashboard from './components/Dashboard/Dashboard';
import PirateProfiler from './components/PirateProfiler/PirateProfiler';

// Shop and membership
import Shop from './components/Shop/Shop';
import IjrrGear from './components/IjrrGear/IjrrGear';
import IjrrGearProductDetail from './components/IjrrGear/SingleProduct/IjrrGearProductDetail';
import Pricing from './components/Pricing/Pricing';
import Membership from './components/Membership/Membership';
import CustomizeMembership from './components/CustomizeMembership/CustomizeMembership';
import ShoppingCart from './components/ShoppingCart/ShoppingCart';

// Auth logic
import Redirector from './components/Auth/Redirector';

// Layout wrapper for all internal sections (with header/footer)
function Layout() {
  const location = useLocation();

  // *** JAVASCRIPT LAYOUT FIX ***
  // This calculates the screen height and forces the footer to the bottom
  useEffect(() => {
    const adjustFooterPosition = () => {
      const footer = document.querySelector('.cdx-footer');
      const main = document.querySelector('.main-content-area');

      if (footer && main) {
        const footerHeight = footer.offsetHeight;
        // Logic: Main Height = Window Height - Footer Height.
        // The Header is 'fixed', so it floats on top. Main already has padding-top to accommodate it.
        main.style.minHeight = `calc(100vh - ${footerHeight}px)`;
      }
    };

    // Run on load
    adjustFooterPosition();

    // Run whenever the window is resized
    window.addEventListener('resize', adjustFooterPosition);

    // Cleanup listener on unmount
    return () => window.removeEventListener('resize', adjustFooterPosition);
  }, [location.pathname]); // Re-run math every time the URL changes

  return (
    <>
      <Header />
      {/* 'main-content-area' is now controlled by the JS script above.
         'content-area-padding' ensures content isn't hidden behind the fixed Header.
      */}
      <main className="main-content-area content-area-padding">
        <Routes>
          {/* Home */}
          <Route
            path="home"
            element={
              <Redirector>
                <Home key={location.pathname} />
              </Redirector>
            }
          />

          {/* Public pages */}
          <Route path="about" element={<About />} />
          <Route path="tradition" element={<Tradition />} />
          <Route path="maps" element={<Maps />} />
          <Route path="contact" element={<ContactUs />} />
          <Route path="club" element={<JollyRogerClub />} />

          {/* Legal */}
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="commitments" element={<Commitments />} />

          {/* Authentication & user */}
          <Route
            path="register"
            element={
              <Redirector reverse>
                <Register />
              </Redirector>
            }
          />
          <Route
            path="login"
            element={
              <Redirector reverse>
                <Login />
              </Redirector>
            }
          />
          <Route path="profile" element={<Profile />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="pirate-profiler" element={AI_ENABLED ? <PirateProfiler /> : <Dashboard />} />

          {/* Shop and membership */}
          <Route path="shop" element={<Shop />} />
          <Route path="ijrr-gear" element={<IjrrGear />} />
          <Route path="ijrr-gear/:modelId/:slug/:colorIndex" element={<IjrrGearProductDetail />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="membership" element={<Membership />} />
          <Route path="customize-membership" element={<CustomizeMembership />} />
          <Route path="shopping-cart" element={<ShoppingCart />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

// Main App component with base routing logic
function App() {
  return (
    <Router>
      <Routes>
        {/* Root redirect */}
        <Route
          path="/"
          element={
            <Redirector>
              <CustomIndex />
            </Redirector>
          }
        />

        {/* Layout with all other routes */}
        <Route path="*" element={<Layout />} />
      </Routes>
    </Router>
  );
}

export default App;